import { z } from 'zod';
import { DocId, IsoTimestamp, TenantId } from '../common.js';
import { BON_EXT, BON_MAX_BYTES } from './declaratie.js';
/**
 * Bijlage bij een DM. Zelfde toegestane types en 10 MB-grens als
 * declaratiebonnen (PDF, afbeelding, Word, Excel). `object` is het GCS-pad in
 * de privé-bijlagenbucket, server-gemunt als `{conversationId}/{userId}/{uuid}.{ext}`;
 * `url` is een kortlevende signed read URL die alleen in responses zit, nooit
 * in Firestore.
 */
export const DM_BIJLAGEN_MAX = 5;
export const DmBijlage = z.object({
    object: z.string().min(1).max(300),
    filename: z.string().min(1).max(200),
    mimeType: z.string().refine((v) => v in BON_EXT, {
        message: 'Alleen PDF, afbeelding, Word of Excel',
    }),
    sizeBytes: z.number().int().positive().max(BON_MAX_BYTES),
    url: z.string().optional(),
});
/**
 * A direct-message conversation between exactly two SUPPOHRT-intern users.
 *
 * Firestore path: tenants/{tenantId}/dm_conversations/{conversationId}
 * conversationId = sorted([userId1, userId2]).join('__') — deterministic so
 * a "Stuur bericht" from the Smoelenboek never creates a duplicate conversation.
 */
export const DmConversationSchema = z.object({
    id: DocId,
    tenantId: TenantId,
    participants: z.tuple([DocId, DocId]),
    lastMessageAt: IsoTimestamp.optional(),
    lastMessagePreview: z.string().max(80).optional(),
    unreadCounts: z.record(z.string(), z.number().int().min(0)).default({}),
    /**
     * Per deelnemer: wanneer die het gesprek voor het laatst opende. Een eigen
     * bericht is "gelezen" zodra de ander na createdAt keek. Ontbreekt op oude
     * docs, en dat betekent gewoon: nog niet gelezen.
     */
    lastReadAt: z.record(z.string(), IsoTimestamp).default({}),
    /**
     * Per deelnemer: wanneer die het gesprek verborg. Het blijft weg tot er een
     * bericht na dat moment komt; dan verschijnt het vanzelf weer in de lijst.
     */
    hiddenBy: z.record(z.string(), IsoTimestamp).default({}),
    createdAt: IsoTimestamp,
});
/**
 * A single message within a conversation.
 *
 * Firestore path: tenants/{tenantId}/dm_conversations/{conversationId}/dm_messages/{messageId}
 */
export const DmMessageSchema = z.object({
    id: DocId,
    conversationId: DocId,
    senderId: DocId,
    // Leeg mag als er bijlagen zijn.
    text: z.string().max(4000),
    bijlagen: z.array(DmBijlage).max(DM_BIJLAGEN_MAX).optional(),
    createdAt: IsoTimestamp,
    readAt: IsoTimestamp.optional(),
});
export const SendMessageInput = z.object({
    text: z.string().min(1).max(4000).trim(),
    /**
     * Door de client gekozen id (uuid). Opnieuw proberen na een timeout stuurt
     * hetzelfde id mee, zodat de server het bericht niet twee keer opslaat.
     */
    id: z.string().uuid().optional(),
});
/** DM's mogen ook alleen bijlagen bevatten; kanalen gebruiken SendMessageInput. */
export const SendDmMessageInput = z
    .object({
    text: z.string().max(4000).trim().default(''),
    bijlagen: z
        .array(DmBijlage.omit({ url: true }))
        .max(DM_BIJLAGEN_MAX)
        .default([]),
    id: z.string().uuid().optional(),
})
    .refine((v) => v.text.length > 0 || v.bijlagen.length > 0, {
    message: 'Een bericht heeft tekst of een bijlage nodig',
});
export const DmBijlageUploadInput = DmBijlage.pick({
    filename: true,
    mimeType: true,
    sizeBytes: true,
});
/**
 * Tot welk bericht de lezer daadwerkelijk keek (createdAt van het nieuwste
 * bericht dat de client rendert). Zonder upTo stempelt de server "nu", en dan
 * telt een bericht dat tussen GET en /read binnenkwam al als gelezen.
 */
export const MarkReadInput = z.object({
    upTo: IsoTimestamp.optional(),
});
/** Tot welk lastMessageAt de client het gesprek zag; nieuwere berichten houden het zichtbaar. */
export const HideConversationInput = z.object({
    upTo: IsoTimestamp.optional(),
});
export const SearchMessagesQuery = z.object({
    q: z.string().trim().min(2).max(100),
});
/** Eén zoektreffer: een DM-gesprek of een kanaal, plus het bericht zelf. */
export const MessageSearchHit = z.object({
    kind: z.enum(['dm', 'channel']),
    /** conversationId of threadId, om het gesprek te openen. */
    id: DocId,
    /** Kanaalnaam; voor DM's zoekt de client de naam zelf op via participants. */
    title: z.string().optional(),
    participants: z.array(DocId).optional(),
    message: z.object({ id: DocId, senderId: DocId, text: z.string(), createdAt: IsoTimestamp }),
});
export const StartConversationInput = z.object({
    otherUserId: DocId,
});
//# sourceMappingURL=dm.js.map